import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUsers, FiAward, FiTarget, FiHeart } = FiIcons;

function About() {
  const values = [
    {
      icon: FiTarget,
      title: "Our Mission",
      description: "To accelerate the world's transition to sustainable energy through innovative solar solutions and exceptional service."
    },
    {
      icon: FiHeart,
      title: "Our Vision",
      description: "A world powered by clean, renewable energy where every home and business contributes to a sustainable future."
    },
    {
      icon: FiAward,
      title: "Our Values",
      description: "Quality, integrity, innovation, and customer satisfaction drive everything we do in the solar energy industry."
    }
  ];

  const team = [
    {
      name: "John Smith",
      position: "CEO & Founder",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
      description: "15+ years in renewable energy sector"
    },
    {
      name: "Sarah Johnson",
      position: "Chief Technology Officer",
      image: "https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
      description: "Expert in solar technology and innovation"
    },
    {
      name: "Michael Brown",
      position: "Head of Operations",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
      description: "Specialist in project management and execution"
    }
  ];

  return (
    <>
      <Helmet>
        <title>About Us - SolarTech Solutions | Leading Solar Energy Company</title>
        <meta name="description" content="Learn about SolarTech Solutions - a leading solar energy company with 15+ years of experience. Meet our expert team and discover our mission for sustainable energy." />
        <meta name="keywords" content="about solar company, solar energy experts, renewable energy team, solar installation company history" />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-blue-50 to-orange-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              About <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">SolarTech Solutions</span>
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed">
              We are a leading provider of solar energy solutions with over 15 years of experience 
              in helping homes and businesses transition to clean, renewable energy. Our commitment 
              to quality, innovation, and customer satisfaction has made us a trusted name in the solar industry.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">Our Story</h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Founded in 2008, SolarTech Solutions began with a simple mission: to make solar energy 
                accessible and affordable for everyone. What started as a small team of renewable energy 
                enthusiasts has grown into one of the region's most trusted solar installation companies.
              </p>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Over the years, we have installed over 500 solar systems, helping our customers save 
                millions of dollars on their energy bills while reducing their carbon footprint. 
                Our commitment to excellence and customer satisfaction has earned us numerous industry awards 
                and the trust of thousands of satisfied customers.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <h3 className="text-3xl font-bold text-orange-500">500+</h3>
                  <p className="text-gray-600">Projects Completed</p>
                </div>
                <div className="text-center">
                  <h3 className="text-3xl font-bold text-orange-500">15+</h3>
                  <p className="text-gray-600">Years Experience</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <img
                src="https://images.unsplash.com/photo-1497435334941-8c899ee9e8e9?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Solar panel installation team"
                className="rounded-2xl shadow-lg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Our Mission, Vision & Values
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We are driven by our commitment to creating a sustainable future through innovative solar solutions
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className="bg-white p-8 rounded-2xl shadow-lg text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={value.icon} className="text-white text-2xl" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Meet Our Expert Team
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our experienced professionals are dedicated to providing you with the best solar energy solutions
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className="text-center"
              >
                <div className="relative mb-6">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-32 h-32 rounded-full mx-auto object-cover"
                  />
                  <div className="absolute inset-0 w-32 h-32 rounded-full mx-auto bg-gradient-to-br from-orange-500/20 to-yellow-500/20"></div>
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{member.name}</h3>
                <p className="text-orange-500 font-medium mb-2">{member.position}</p>
                <p className="text-gray-600 text-sm">{member.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-500 to-yellow-500">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Join Our Solar Family?
            </h2>
            <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
              Let our experienced team help you make the switch to clean, renewable solar energy. 
              Contact us today for a free consultation!
            </p>
            <button className="bg-white text-orange-500 px-8 py-4 rounded-full font-semibold hover:shadow-lg transition-shadow">
              Get Free Consultation
            </button>
          </motion.div>
        </div>
      </section>
    </>
  );
}

export default About;